import React, { useRef, useState } from "react";
import { TbList } from "react-icons/tb";
import { BiFolder } from "react-icons/bi";
import { MdPlaylistRemove } from "react-icons/md";
import { FiX, FiUploadCloud } from "react-icons/fi";

import ErrorMessage from "../../Components/ErrorMessage";
import Spinner from "../../Components/Spinner";
import { useLanguage } from "../../Translations/LenguageContext";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;

const DocumentsSource = ({
    account_id,
    org_id,
    context_id,
    fetchRecords,
    closeModal,
    handleBack,
}) => {
    const { t } = useLanguage()
    const [files, setFiles] = useState([]);
    const [loadingExtract, setLoadingExtract] = useState(false);

    const [errorMessage, setErrorMessage] = useState({
        message: "",
        display: false,
    });

    const fileInputRef = useRef(null);

    const clickFileInput = () => {
        fileInputRef.current?.click();
    };

    const handleFileSelection = (event) => {
        const selectedFiles = Array.from(event.target.files || []);

        setFiles((previousFiles) => {
            const existingKeys = new Set(
                previousFiles.map(
                    (file) => `${file.name}-${file.size}-${file.lastModified}`
                )
            );

            const newFiles = selectedFiles.filter(
                (file) =>
                    !existingKeys.has(
                        `${file.name}-${file.size}-${file.lastModified}`
                    )
            );

            return [...previousFiles, ...newFiles];
        });

        setErrorMessage({
            message: "",
            display: false,
        });

        event.target.value = "";
    };

    const removeFile = (indexToRemove) => {
        setFiles((previousFiles) =>
            previousFiles.filter(
                (_, index) => index !== indexToRemove
            )
        );
    };

    const handleSelectAll = () => {
        if (files.length > 0) {
            setFiles([]);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) {
            return `${bytes} B`;
        }

        if (bytes < 1024 * 1024) {
            return `${(bytes / 1024).toFixed(1)} KB`;
        }

        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    };

    const uploadDocuments = async () => {
        if (files.length === 0) {
            setErrorMessage({
                message: t.context.please_select_file,
                display: true,
            });

            return;
        }

        setLoadingExtract(true);

        setErrorMessage({
            message: "",
            display: false,
        });

        try {
            const formData = new FormData();

            formData.append("account_id", account_id);
            formData.append("org_id", org_id);
            formData.append("context_id", context_id);

            files.forEach((file) => {
                formData.append("files", file);
            });

            const response = await fetch(
                `${GEN_BACKEND_LINK}/upload_documents`,
                {
                    method: "POST",
                    body: formData,
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(
                    data.message ||
                    data.error ||
                    t.context.failed_upload
                );
            }

            closeModal();
            await fetchRecords();

        } catch (error) {
            setErrorMessage({
                message: error.message || "Server error",
                display: true,
            });
        } finally {
            setLoadingExtract(false);
        }
    };

    return (
        <div className="source_cont">
            <p className="source_cont_website_title">
                {t.context.add_docs_title}
            </p>

            <p className="source_cont_website_subtitle">
                {t.context.add_docs_description}
            </p>

            <div className="source_website_input_cont">
                <input
                    ref={fileInputRef}
                    type="file"
                    className="file_input"
                    id="file_input"
                    multiple
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt,.md,.rtf,.odt,.ods,.odp"
                    onChange={handleFileSelection}
                />

                <button
                    type="button"
                    className="source_file_btn"
                    onClick={clickFileInput}
                    disabled={loadingExtract}
                >
                    <BiFolder /> {t.context.select_files}
                </button>
            </div>

            <div className="source_website_pages_list">
                {files.length === 0 ? (
                    <div className="source_website_pages_list_empty">
                        <p>
                            <i>
                                <TbList />
                            </i>{" "}
                            {t.context.selected_files_appear}
                        </p>
                    </div>
                ) : (
                    <div className="source_website_pages_list_urls fade-in">
                        <div className="source_website_pages_list_header">
                            <button
                                type="button"
                                className="source_website_pages_list_header_btn"
                                onClick={handleSelectAll}
                                disabled={loadingExtract}
                            >
                                <MdPlaylistRemove /> {t.context.remove_all}
                            </button>

                            <p>
                                {files.length}{" "}
                                {files.length === 1 ? "file" : "files"} {t.context.selected}
                            </p>
                        </div>

                        <div className="source_website_pages_list_urls_inner">
                            {files.map((file, index) => (
                                <div
                                    key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                                    className="source_website_page_item"
                                >
                                    <div className="document_file_details">
                                        <p className="document_file_details_item" title={file.name}>
                                            {file.name}
                                        </p>
                                        <span>
                                            {formatFileSize(file.size)}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className="context_window_footer_btn_delete"
                                        onClick={() => removeFile(index)}
                                        disabled={loadingExtract}
                                        aria-label={`${t.context.remove} ${file.name}`}
                                    >
                                        <FiX />
                                    </button>

                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {errorMessage.display && (
                <ErrorMessage err={errorMessage} />
            )}

            {files.length > 0 && (
                <button
                    type="button"
                    className="source_run_btn fade-in"
                    onClick={uploadDocuments}
                    disabled={loadingExtract}
                >
                    {loadingExtract ? (
                        <Spinner loading={loadingExtract} />
                    ) : (
                        <>
                            <FiUploadCloud /> {t.context.upload_documents}
                        </>
                    )}
                </button>
            )}
        </div>
    );
};

export default DocumentsSource;